<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import {
  FormLabel,
  FormInput,
  FormCheck
} from "../../../../base-components/Form";
import Alert from "../../../../base-components/Alert";
import Button from "../../../../base-components/Button";
import { computed, onBeforeMount, reactive, ref, toRefs } from "vue";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import useVuelidate from "@vuelidate/core";
import Lucide from "../../../../base-components/Lucide";
import TomSelect from "../../../../custom-components/TomSelect";
import { useMensajesStore } from "../../../../services/mensajes";

import { useUsuarioRolesStore } from "../../../../services/usuarioRoles";
import { useUsuariosStore } from "../../../../services/usuarios";
import { LogAcciones, Usuario } from "@fixeduy/restaurantestypes";
import { getCurrentTime } from "../../../../utils/fecha";
import { userUserStore } from "../../../../stores/user";
import { useLogAccionesStore } from "../../../../services/logAcciones";

interface Props {
  usuario: Usuario;
}

const props = defineProps<Props>();
const mensajesStore = useMensajesStore();
const usuariosRolesStore = useUsuarioRolesStore();
const usuariosStore = useUsuariosStore();
const userStore = userUserStore();
const logAccionesStore = useLogAccionesStore();
const cargando = ref(false);

onBeforeMount(() => {
  usuariosRolesStore.getRolesFromFirestore();
});

const emit = defineEmits<{ (e: "cerrar"): void }>();

const formData = reactive({
  nombre: props.usuario.nombre,
  activo: props.usuario.activo,
  rolId: props.usuario.rolId
});

const rules: any = computed(() => {
  return {
    vendedor: {},
    vendedorRestringido: {},
    activo: {},
    rolId: {},
    locales: {}
  };
});

const validate$ = useVuelidate(rules, toRefs(formData));

const handleEdit = async () => {
  cargando.value = true;
  const res = await usuariosStore.actualizar(
    props.usuario,
    formData.nombre,
    formData.activo,
    formData.rolId
  );
  if (res.exito) {
    const logAcciones: LogAcciones = {
      id: "",
      accion: [
        `Se edita usuario ${props.usuario.nombre} (${props.usuario.id}).`
      ],
      modulo: "usuarios",
      fecha: getCurrentTime(),
      usuario: userStore.userLoggedIn?.uid!,
      mail: userStore.userLoggedIn?.email!
    };

    await logAccionesStore.guardarLog(logAcciones);
    mensajesStore.mostrarToast(
      "Usuario actualizado",
      `${props.usuario.nombre}`,
      mensajesStore.mensajeType.exito
    );
  } else {
    mensajesStore.mostrarModal(
      "Error actualizando el rol",
      `Desde el servidor se recibió el mensaje de error: \n${res.errorMsg}.\nConsulta al chat de ayuda, el botón celeste abajo a la derecha.`,
      mensajesStore.mensajeType.error
    );
  }
  cargando.value = false;
  emit("cerrar");
};
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="lg">
    <Dialog.Panel>
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">Editar usuario</h2>
      </Dialog.Title>
      <!-------------------- DIALOG DESCRIPTION ------------------------>

      <Dialog.Description>
        <!-------------------- nombre ------------------------>
        <div class="flex">
          <div class="flex-none w-50 pr-5 mt-2 text-right">
            <FormLabel htmlFor="horizontal-form-1 ml-1" class="w-40 sw:w-20">
              Nombre</FormLabel
            >
          </div>
          <div class="flex-1">
            <FormInput type="text" v-model="formData.nombre" />
          </div>
        </div>

        <!-------------------- rol ------------------------>
        <div class="flex mt-5">
          <div class="flex-none w-50 pr-5 mt-2 text-right">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Rol
            </FormLabel>
          </div>
          <div class="flex-1">
            <TomSelect
              v-model.trim="formData.rolId"
              :class="{
                'border-danger': validate$.rolId?.$error
              }"
              style="width: 50%"
              :options="{ placeholder: 'Selecciona el rol' }"
            >
              <template v-for="rol in usuariosRolesStore.roles" :key="rol.id">
                <option :value="rol.id">{{ rol.rol }}</option>
              </template>
            </TomSelect>
          </div>
        </div>

        <!-------------------- activo ------------------------>
        <div class="flex">
          <div class="flex-none w-50 pr-5 mt-2 text-right">
            <FormLabel htmlFor="horizontal-form-1 ml-1" class="w-40 sw:w-20">
              Activo</FormLabel
            >
          </div>
          <div class="flex-1 ml-2">
            <FormCheck class="mt-2">
              <FormCheck.Input
                id="checkbox-switch-1"
                type="checkbox"
                v-model="formData.activo"
              />
            </FormCheck>
          </div>
        </div>
        <Alert
          variant="danger"
          class="flex items-center mb-2"
          v-if="formData.activo == false && props.usuario.activo"
        >
          <Lucide icon="AlertOctagon" class="w-6 h-6 mr-2" />
          El usuario bloqueado no podrá acceder a esta empresa.
        </Alert>
        <Alert
          variant="danger"
          class="flex items-center mb-2"
          v-if="formData.activo == false && !props.usuario.activo"
        >
          <Lucide icon="AlertOctagon" class="w-6 h-6 mr-2" />
          El usuario se encuentra bloqueado y no puede acceder a esta empresa.
        </Alert>
      </Dialog.Description>

      <!-------------------- DIALOG FOOTER ------------------------>
      <Dialog.Footer>
        <div v-if="cargando">
          <div
            class="flex flex-col items-rigth justify-end col-span-6 sm:col-span-3 xl:col-span-2"
          >
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
        <div v-else class="flex justify-between">
          <!-------------------- Botón Cerrar ------------------------>
          <Button
            variant="secondary"
            class="mb-2 mr-3"
            @click="() => emit('cerrar')"
          >
            Cerrar
          </Button>
          <!-------------------- Botón Guardar ------------------------>
          <Button variant="primary" class="mb-2 mr-1" @click="handleEdit">
            Guardar
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
